import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { Form, Input, Select, Space, Button, DatePicker, Tag, Upload, message, Popconfirm } from 'antd';
import { UploadOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import api from '../../services/api';
import customerApi from '../../services/customerApi';

interface BusinessOverviewTabProps {
  form: any;
  isEdit: boolean;
  isEditing?: boolean;
  canViewCompanyChop?: boolean;
  canEditCompanyChop?: boolean;
  canDeleteCompanyChop?: boolean;
  isCustomerPortal?: boolean;
  customers: Array<{ id: number; name: string }>;
  getDisplayValue: (fieldName: string) => string;
  handleFieldChange: (fieldName: string, value: string) => void;
  getChangeRequestForField: (fieldName: string) => any;
  handleApproveChangeRequest: (requestId: number) => void;
  handleRejectChangeRequest: (requestId: number) => void;
  handleApprovePendingChange: (fieldName: string) => void;
  handleRejectPendingChange: (fieldName: string) => void;
  supportingFiles?: {
    name?: UploadFile[];
    sst_no?: UploadFile[];
    tin_no?: UploadFile[];
    company_chop?: UploadFile[];
  };
  onFileChange?: (field: string, files: UploadFile[]) => void;
  businessId?: number;
  latestRequest?: any;
}

export const BusinessOverviewTab: React.FC<BusinessOverviewTabProps> = ({
  form,
  isEdit,
  isEditing = false,
  canViewCompanyChop = true,
  canEditCompanyChop = true,
  canDeleteCompanyChop = true,
  isCustomerPortal = false,
  customers,
  getDisplayValue,
  handleFieldChange,
  getChangeRequestForField,
  handleApproveChangeRequest,
  handleRejectChangeRequest,
  handleApprovePendingChange,
  handleRejectPendingChange,
  supportingFiles = {},
  onFileChange,
  businessId,
  latestRequest,
}) => {
  const canEditCompanyChopFields = isEditing && canEditCompanyChop;
  const canDeleteCompanyChopField = isEditing && canDeleteCompanyChop;
  const [companyChopPreviewUrl, setCompanyChopPreviewUrl] = useState<string | null>(null);
  const [requestCompanyChopPreviewUrl, setRequestCompanyChopPreviewUrl] = useState<string | null>(null);
  const [fileActionLoading, setFileActionLoading] = useState<Record<string, { view?: boolean; download?: boolean }>>({});
  const hasCompanyChop = (supportingFiles.company_chop || []).length > 0;

  const setFileLoading = (field: string, action: 'view' | 'download', isLoading: boolean) => {
    setFileActionLoading((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [action]: isLoading,
      },
    }));
  };

  // Load company chop preview when file is available
  useEffect(() => {
    let blobUrlToRevoke: string | null = null;
    let isCancelled = false;
    
    const companyChopFile = supportingFiles.company_chop?.[0];
    
    // Clean up previous preview URL
    setCompanyChopPreviewUrl((prevUrl) => {
      if (prevUrl) {
        URL.revokeObjectURL(prevUrl);
      }
      return null;
    });
    
    if (companyChopFile) {
      if (companyChopFile.originFileObj) {
        // New file - create blob URL from originFileObj
        const blobUrl = URL.createObjectURL(companyChopFile.originFileObj);
        if (!isCancelled) {
          setCompanyChopPreviewUrl(blobUrl);
          blobUrlToRevoke = blobUrl;
        } else {
          URL.revokeObjectURL(blobUrl);
        }
      } else if (companyChopFile.url && businessId) {
        // Existing file from S3 - fetch via API and create blob URL
        // Always fetch via API to ensure blob URL is used
        const loadPreview = async () => {
          try {
            const isCustomerPortalPath = window.location.pathname.startsWith('/customer');
            const url = isCustomerPortalPath
              ? `/customer/businesses/${businessId}/supporting-files/company_chop/view`
              : `/businesses/${businessId}/supporting-files/company_chop/view`;
            const apiClient = isCustomerPortalPath ? customerApi : api;
            
            const response = await apiClient.get(url, {
              responseType: 'blob',
            });
            
            if (isCancelled) {
              return;
            }
            
            const blob = new Blob([response.data], { 
              type: response.headers['content-type'] || 'image/png' 
            });
            const blobUrl = URL.createObjectURL(blob);
            setCompanyChopPreviewUrl(blobUrl);
            blobUrlToRevoke = blobUrl;
          } catch (error) {
            if (!isCancelled) {
              // Failed to load preview - try to reload after a short delay
              console.error('Failed to load company chop preview:', error);
              setCompanyChopPreviewUrl(null);
            }
          }
        };
        loadPreview();
      } else if (companyChopFile.url && !businessId) {
        // File has URL but no businessId (might be a new business or file just uploaded)
        // Try to create a blob URL from the URL if it's a data URL or blob URL
        if (companyChopFile.url.startsWith('blob:') || companyChopFile.url.startsWith('data:')) {
          setCompanyChopPreviewUrl(companyChopFile.url);
        } else {
          setCompanyChopPreviewUrl(null);
        }
      } else {
        setCompanyChopPreviewUrl(null);
      }
    } else {
      setCompanyChopPreviewUrl(null);
    }
    
    return () => {
      isCancelled = true;
      if (blobUrlToRevoke) {
        URL.revokeObjectURL(blobUrlToRevoke);
      }
      // Also revoke current preview URL if it exists
      setCompanyChopPreviewUrl((currentUrl) => {
        if (currentUrl && currentUrl.startsWith('blob:')) {
          URL.revokeObjectURL(currentUrl);
        }
        return null;
      });
    };
  }, [supportingFiles.company_chop, businessId]);

  useEffect(() => {
    let blobUrlToRevoke: string | null = null;
    let isCancelled = false;
    const hasRequestedCompanyChop = Boolean(latestRequest?.supporting_company_chop);

    setRequestCompanyChopPreviewUrl((prevUrl) => {
      if (prevUrl) {
        URL.revokeObjectURL(prevUrl);
      }
      return null;
    });

    if (!hasRequestedCompanyChop || !businessId) {
      return () => {
        isCancelled = true;
        if (blobUrlToRevoke) {
          URL.revokeObjectURL(blobUrlToRevoke);
        }
      };
    }

    const loadRequestedPreview = async () => {
      try {
        const response = await api.get(`/business-requests/${businessId}/supporting/company_chop/view`, {
          responseType: 'blob',
        });
        if (isCancelled) return;
        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'image/png',
        });
        const blobUrl = URL.createObjectURL(blob);
        setRequestCompanyChopPreviewUrl(blobUrl);
        blobUrlToRevoke = blobUrl;
      } catch (error) {
        if (!isCancelled) {
          setRequestCompanyChopPreviewUrl(null);
        }
      }
    };

    loadRequestedPreview();

    return () => {
      isCancelled = true;
      if (blobUrlToRevoke) {
        URL.revokeObjectURL(blobUrlToRevoke);
      }
      setRequestCompanyChopPreviewUrl((currentUrl) => {
        if (currentUrl && currentUrl.startsWith('blob:')) {
          URL.revokeObjectURL(currentUrl);
        }
        return null;
      });
    };
  }, [latestRequest?.supporting_company_chop, businessId]);
  const handleView = async (field: string, file: UploadFile) => {
    if (!file) return;
    
    try {
      setFileLoading(field, 'view', true);
      // If it's a new file (has originFileObj), use it directly
      if (file.originFileObj) {
        const blobUrl = URL.createObjectURL(file.originFileObj);
        const newWindow = window.open(blobUrl, '_blank');
        
        // Clean up blob URL after window is closed or after a delay
        if (newWindow) {
          newWindow.addEventListener('beforeunload', () => {
            URL.revokeObjectURL(blobUrl);
          });
        }
        setTimeout(() => {
          try {
            URL.revokeObjectURL(blobUrl);
          } catch (e) {
            // Ignore errors if URL was already revoked
          }
        }, 60000); // 60 seconds
        return;
      }
      
      // For existing files from S3, fetch via API
      if (!businessId) return;
      
      const isCustomerPortalPath = window.location.pathname.startsWith('/customer');
      let url: string;
      let apiClient: any;
      
      if (isCustomerPortalPath) {
        // Customer portal route: /customer/businesses/{id}/supporting-files/{field}/view
        url = `/customer/businesses/${businessId}/supporting-files/${field}/view`;
        apiClient = customerApi;
      } else {
        // Admin portal route: /businesses/{id}/supporting-files/{field}/view
        url = `/businesses/${businessId}/supporting-files/${field}/view`;
        apiClient = api;
      }
      
      // Use axios to fetch file with authentication token (similar to Files module)
      const response = await apiClient.get(url, {
        responseType: 'blob',
      });
      
      // Create blob URL and open in new window
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/octet-stream' 
      });
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Open blob URL in new window
      const newWindow = window.open(blobUrl, '_blank');
      
      // Clean up blob URL after window is closed or after a delay
      if (newWindow) {
        newWindow.addEventListener('beforeunload', () => {
          window.URL.revokeObjectURL(blobUrl);
        });
      }
      // Also clean up after a longer delay as fallback
      setTimeout(() => {
        try {
          window.URL.revokeObjectURL(blobUrl);
        } catch (e) {
          // Ignore errors if URL was already revoked
        }
      }, 60000); // 60 seconds
    } catch (error: any) {
      message.error('Failed to view file');
    } finally {
      setFileLoading(field, 'view', false);
    }
  };

  const formatValue = (fieldName: string, raw: any): string => {
    if (raw === undefined || raw === null || raw === '') return '-';
    if (
      fieldName === 'incorporation_date' ||
      fieldName === 'year_end'
    ) {
      const d = dayjs(raw);
      return d.isValid() ? d.format('DD/MM/YYYY') : raw.toString();
    }
    return typeof raw === 'string' ? raw : raw.toString();
  };

  const getCurrentValue = (fieldName: string): string => {
    const val = form.getFieldValue(fieldName);
    return formatValue(fieldName, val);
  };

  const getPendingValue = (fieldName: string): string | null => {
    const val = getDisplayValue(fieldName);
    if (!val) return null;
    return formatValue(fieldName, val);
  };

  // In create mode (!isEdit), always allow editing. In edit mode, use isEditing state.
  const canEdit = !isEdit || isEditing;

  const getRequestedValue = (fieldName: string): any => {
    if (!latestRequest) return null;
    if (fieldName === 'former_name') return latestRequest.former_name_text ?? null;
    return (latestRequest as any)[fieldName] ?? null;
  };

  const handleViewRequestedFile = async (field: string) => {
    if (!businessId || isCustomerPortal) return;
    try {
      setFileLoading(`request_${field}`, 'view', true);
      const response = await api.get(`/business-requests/${businessId}/supporting/${field}/view`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error: any) {
      message.error('Failed to view requested file');
    } finally {
      setFileLoading(`request_${field}`, 'view', false);
    }
  };

  const renderFileUploadButtons = (fileField: string) => {
    const files = supportingFiles[fileField as keyof typeof supportingFiles];
    const hasFile = files && files.length > 0;
    const requestedFieldMap: Record<string, string> = {
      name: 'supporting_name',
      sst_no: 'supporting_sst_no',
      tin_no: 'supporting_tin_no',
      trademark_logo: 'supporting_trademark_logo',
      industry_specific_license: 'supporting_industry_specific_license',
      company_chop: 'supporting_company_chop',
    };
    const requestedKey = requestedFieldMap[fileField];
    const hasRequestedFile = !!(latestRequest && requestedKey && latestRequest[requestedKey]);

    return (
      <Space>
        {isEditing && (
          <Upload
            fileList={files || []}
            showUploadList={false}
            beforeUpload={() => false}
            onChange={(info) => {
              if (onFileChange) {
                onFileChange(fileField, info.fileList);
              }
            }}
            maxCount={1}
            accept=".update,.png,.jpg,.jpeg"
          >
            <Button
              size="small"
              icon={<UploadOutlined />}
              style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
            />
          </Upload>
        )}
        {hasFile && (
          <>
            <Button
              size="small"
              icon={<EyeOutlined />}
              loading={fileActionLoading[fileField]?.view}
              style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
              onClick={() => {
                const file = files?.[0];
                if (file && businessId) {
                  handleView(fileField, file);
                }
              }}
            />
            {hasRequestedFile && (
              <Button
                size="small"
                icon={<EyeOutlined />}
                loading={fileActionLoading[`request_${fileField}`]?.view}
                style={{ width: 28, height: 28, padding: 0, borderRadius: 999, borderColor: '#fadb14', color: '#d4a106' }}
                onClick={() => handleViewRequestedFile(fileField)}
              />
            )}
            {isEditing && (
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
                onClick={() => {
                  if (onFileChange) {
                    onFileChange(fileField, []);
                  }
                }}
              />
            )}
          </>
        )}
        {!hasFile && hasRequestedFile && (
          <Button
            size="small"
            icon={<EyeOutlined />}
            loading={fileActionLoading[`request_${fileField}`]?.view}
            style={{ width: 28, height: 28, padding: 0, borderRadius: 999, borderColor: '#fadb14', color: '#d4a106' }}
            onClick={() => handleViewRequestedFile(fileField)}
          />
        )}
      </Space>
    );
  };

  const renderFieldRow = (
    label: string,
    fieldName: string,
    renderValue?: () => React.ReactNode,
    renderEdit?: () => React.ReactNode,
    required?: boolean,
    showFileUpload?: boolean,
    fileField?: string
  ) => {
    const currentValue = renderValue ? renderValue() : getCurrentValue(fieldName);
    const pendingValue = getPendingValue(fieldName);
    const changeRequest = getChangeRequestForField(fieldName);

    const hasPending = pendingValue !== null && pendingValue !== '' && pendingValue !== getCurrentValue(fieldName);
    const requestedValue = getRequestedValue(fieldName);
    const normalizedCurrentValue = (currentValue ?? '').toString();
    const normalizedRequestedValue = requestedValue === null || requestedValue === undefined ? '' : String(requestedValue);
    const hasRequestedValue = normalizedRequestedValue !== '' && normalizedRequestedValue !== normalizedCurrentValue;

    const renderAction = () => {
      if (!hasPending) return null;
      
      // Customer portal: show pending status tag
      if (isCustomerPortal) {
        return <Tag color="orange">Pending</Tag>;
      }
      
      // Admin panel: show approve/reject buttons
    return (
                <Space>
                  <Button
                    type="primary"
                    size="small"
            style={{ 
              backgroundColor: '#52c41a', 
              borderColor: '#52c41a',
              pointerEvents: 'auto',
              cursor: 'pointer',
            }}
            disabled={false}
            onClick={(e) => {
              e.stopPropagation();
                      if (changeRequest) {
                        handleApproveChangeRequest(changeRequest.id);
              } else {
                        handleApprovePendingChange(fieldName);
                      }
                    }}
                  >
                    Approve
                  </Button>
                  <Button
                    danger
                    size="small"
            style={{
              pointerEvents: 'auto',
              cursor: 'pointer',
            }}
            disabled={false}
            onClick={(e) => {
              e.stopPropagation();
                      if (changeRequest) {
                        handleRejectChangeRequest(changeRequest.id);
                      } else {
                        handleRejectPendingChange(fieldName);
                      }
                    }}
                  >
                    Reject
                  </Button>
                </Space>
      );
    };

    const renderPending = () => {
      if (canEdit && renderEdit) {
        return renderEdit();
      }
      if (!hasPending) return currentValue;
      return (
        <div>
          <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>
            {changeRequest?.old_value || currentValue || '-'}
          </span>
          <span style={{ color: '#ff9800' }}>
            {changeRequest?.new_value || pendingValue || '-'}
          </span>
        </div>
      );
    };

    // Show action column only in edit mode when there are pending changes
    const showActionColumn = isEdit && isEditing && hasPending;

    return (
      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
        <td style={{ width: '22%', fontWeight: 600, padding: '12px', backgroundColor: '#fafafa' }}>
          {label}
          {required && <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>}
        </td>
        <td style={{ width: showActionColumn ? '38%' : showFileUpload ? '53%' : '78%', padding: '12px' }}>
          {renderPending()}
          {hasRequestedValue && (
            <div style={{ marginTop: 6, fontSize: 12, color: '#faad14' }}>
              Requested: {normalizedRequestedValue}
            </div>
          )}
        </td>
        {showFileUpload && fileField && (
          <td style={{ width: '25%', padding: '12px' }}>
            {renderFileUploadButtons(fileField)}
          </td>
        )}
        {showActionColumn && !showFileUpload && <td style={{ width: '25%', padding: '12px' }}>{renderAction()}</td>}
        {showActionColumn && showFileUpload && <td style={{ width: '15%', padding: '12px' }}>{renderAction()}</td>}
      </tr>
    );
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>{[
          renderFieldRow('Name', 'name', undefined, () => (
            <Form.Item 
              name="name" 
              style={{ margin: 0 }}
            >
              <Input 
                onChange={(e) => isCustomerPortal && handleFieldChange('name', e.target.value)}
              />
            </Form.Item>
          ), true, true, 'name'), /* required, showFileUpload, fileField */
          renderFieldRow('Former Name', 'former_name', undefined, () => (
            <Form.Item name="former_name" style={{ margin: 0 }}>
              <Input />
            </Form.Item>
          )),
          renderFieldRow('Registration No.', 'registration_no', undefined, () => (
            <Form.Item name="registration_no" style={{ margin: 0 }}>
              <Input 
                onChange={(e) => isCustomerPortal && handleFieldChange('registration_no', e.target.value)}
              />
            </Form.Item>
          )),
          renderFieldRow('Entity', 'entity', undefined, () => (
            <Form.Item name="entity" style={{ margin: 0 }}>
        <Select
          placeholder="Select entity"
          allowClear
                onChange={(value) => isCustomerPortal && handleFieldChange('entity', value || '')}
        >
          <Select.Option value="Sole Proprietorship">Sole Proprietorship</Select.Option>
          <Select.Option value="Partnership">Partnership</Select.Option>
          <Select.Option value="Private Limited Company">Private Limited Company</Select.Option>
          <Select.Option value="Limited Liability Partnership (LLP)">Limited Liability Partnership (LLP)</Select.Option>
        </Select>
            </Form.Item>
          )),
          renderFieldRow('Tin No.', 'tin_no', undefined, () => (
            <Form.Item name="tin_no" style={{ margin: 0 }}>
              <Input 
                onChange={(e) => isCustomerPortal && handleFieldChange('tin_no', e.target.value)}
              />
            </Form.Item>
          ), false, true, 'tin_no'), /* showFileUpload, fileField */
          renderFieldRow('Incorporation', 'incorporation_date', undefined, () => (
            <Form.Item name="incorporation_date" style={{ margin: 0 }}>
              <DatePicker 
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                onChange={(date) => isCustomerPortal && handleFieldChange('incorporation_date', date ? date.format('YYYY-MM-DD') : '')}
              />
          </Form.Item>
          )),
          renderFieldRow('Year End', 'year_end', undefined, () => (
            <Form.Item name="year_end" style={{ margin: 0 }}>
              <Input 
                onChange={(e) => isCustomerPortal && handleFieldChange('year_end', e.target.value)}
              />
          </Form.Item>
          )),
          renderFieldRow('Contact No.', 'contact_number', undefined, () => (
            <Form.Item name="contact_number" style={{ margin: 0 }}>
              <Input 
                onChange={(e) => isCustomerPortal && handleFieldChange('contact_number', e.target.value)}
              />
          </Form.Item>
          )),
          renderFieldRow('Email', 'email', undefined, () => (
            <Form.Item name="email" style={{ margin: 0 }}>
              <Input 
                onChange={(e) => isCustomerPortal && handleFieldChange('email', e.target.value)}
              />
          </Form.Item>
          ))
        ]}</tbody>
      </table>

      {/* Company Chop Section */}
      {canViewCompanyChop && (
        <div style={{ marginTop: 24 }}>
          <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <strong>Company Chop</strong>
            {canEditCompanyChopFields && (
              <Space>
                <Upload
                  fileList={supportingFiles.company_chop || []}
                  showUploadList={false}
                  onChange={(info) => {
                    if (onFileChange) {
                      onFileChange('company_chop', info.fileList);
                    }
                  }}
                  beforeUpload={() => false}
                  maxCount={1}
                  accept="image/png,image/jpeg,image/jpg"
                >
                  <Button size="small" icon={<UploadOutlined />}>
                    Upload Photo
                  </Button>
                </Upload>
                {hasCompanyChop && canDeleteCompanyChopField && (
                  <Popconfirm
                    title="Delete company chop?"
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => onFileChange && onFileChange('company_chop', [])}
                  >
                    <Button size="small" danger>
                      Delete
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            )}
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ border: '1px solid #d9d9d9', padding: '20px', minHeight: '150px', width: '100%', maxWidth: '500px', backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {supportingFiles.company_chop?.[0] ? (
                <div style={{ width: '100%', textAlign: 'center' }}>
                  {companyChopPreviewUrl ? (
                    <img
                      src={companyChopPreviewUrl}
                      alt="Company Chop"
                      style={{ maxWidth: '100%', maxHeight: '200px', cursor: 'pointer' }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const file = supportingFiles.company_chop?.[0];
                        if (file && businessId) {
                          handleView('company_chop', file);
                        }
                      }}
                      onError={() => {
                        // If blob URL fails, try to reload
                        setCompanyChopPreviewUrl(null);
                      }}
                    />
                  ) : (
                    <div style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                      Loading preview...
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                  {canEditCompanyChopFields ? 'No company chop uploaded' : 'No company chop'}
                </div>
              )}
            </div>
            {!isEditing && latestRequest?.supporting_company_chop && (
              <div style={{ border: '1px solid #fadb14', padding: '20px', minHeight: '150px', width: '100%', maxWidth: '500px', backgroundColor: '#fffbe6', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                <div style={{ position: 'absolute', top: 8, left: 12, fontSize: 12, color: '#d4a106', fontWeight: 600 }}>
                  Requested
                </div>
                {requestCompanyChopPreviewUrl ? (
                  <img
                    src={requestCompanyChopPreviewUrl}
                    alt="Company Chop (Request)"
                    style={{ maxWidth: '100%', maxHeight: '200px', cursor: 'pointer' }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleViewRequestedFile('company_chop');
                    }}
                  />
                ) : (
                  <div style={{ color: '#d4a106', textAlign: 'center', padding: '20px' }}>
                    Loading request preview...
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
